/**
 * Firebase Authentication Error Handler
 * Converts Firebase error codes to user-friendly messages following industry standards
 */

/**
 * Get user-friendly error message from Firebase auth error
 * @param {Error} error - Firebase authentication error
 * @returns {string} User-friendly error message
 */
export const getAuthErrorMessage = (error) => {
  // Handle Firebase auth errors
  if (error?.code) {
    switch (error.code) {
      // Email/Password Sign In Errors
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support for assistance.';
      
      case 'auth/user-not-found':
        return 'No account found with this email address. Please check your email or sign up.';
      
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again or reset your password.';
      
      case 'auth/invalid-credential':
        return 'Invalid email or password. Double-check your credentials, or sign up if you don\'t have an account.';
      
      case 'auth/too-many-requests':
        return 'Too many unsuccessful login attempts. Please try again later or reset your password.';
      
      // Registration Errors
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Please sign in or use a different email.';
      
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters with a mix of letters and numbers.';
      
      case 'auth/operation-not-allowed':
        return 'Email/password authentication is not enabled. Please contact support.';
      
      // Network Errors
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      
      case 'auth/timeout':
        return 'Request timed out. Please check your internet connection and try again.';
      
      // Token/Session Errors
      case 'auth/invalid-id-token':
        return 'Your session has expired. Please sign in again.';
      
      case 'auth/user-token-expired':
        return 'Your session has expired. Please sign in again.';
      
      case 'auth/requires-recent-login':
        return 'This operation requires recent authentication. Please sign in again.';
      
      // General Errors
      case 'auth/internal-error':
        return 'An internal error occurred. Please try again later.';
      
      case 'auth/popup-blocked':
        return 'Pop-up was blocked by your browser. Please allow pop-ups for this site.';
      
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      
      case 'auth/cancelled-popup-request':
        return 'Sign-in was cancelled. Please try again.';
      
      // Password Reset Errors
      case 'auth/expired-action-code':
        return 'This link has expired. Please request a new password reset link.';
      
      case 'auth/invalid-action-code':
        return 'This link is invalid or has already been used. Please request a new link.';
      
      // Multi-factor Authentication Errors
      case 'auth/multi-factor-auth-required':
        return 'Additional authentication is required. Please complete multi-factor authentication.';
      
      case 'auth/maximum-second-factor-count-exceeded':
        return 'Maximum number of second factors exceeded.';
      
      case 'auth/second-factor-already-in-use':
        return 'This second factor is already in use.';
      
      // Missing Credentials
      case 'auth/missing-email':
        return 'Please enter your email address.';
      
      case 'auth/missing-password':
        return 'Please enter your password.';
      
      // Account Management
      case 'auth/credential-already-in-use':
        return 'This credential is already associated with a different account.';
      
      case 'auth/account-exists-with-different-credential':
        return 'An account already exists with the same email but different sign-in credentials.';
      
      // Unknown Firebase error with code
      default:
        if (error.code.startsWith('auth/')) {
          return 'Authentication error. Please try again or contact support if the problem persists.';
        }
        return error.message || 'An unexpected error occurred. Please try again.';
    }
  }
  
  // Handle generic JavaScript errors
  if (error?.message) {
    // Check for common network error messages
    if (error.message.includes('Failed to fetch') || 
        error.message.includes('Network request failed')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    
    if (error.message.includes('timeout')) {
      return 'Request timed out. Please try again.';
    }
    
    // Return the error message if it looks user-friendly (not too technical)
    if (error.message.length < 200 && !error.message.includes('at ')) {
      return error.message;
    }
  }
  
  // Fallback for unknown errors
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Get user-friendly error message for backend API errors
 * @param {Object} errorResponse - Backend error response
 * @returns {string} User-friendly error message
 */
export const getBackendErrorMessage = (errorResponse) => {
  // Handle specific error codes from backend
  if (errorResponse?.code) {
    switch (errorResponse.code) {
      case 'E_UNVERIFIED_EMAIL':
        return 'Please verify your email before signing in. Check your inbox for the verification email.';
      
      case 'E_INVALID_CREDENTIALS':
        return 'Invalid email or password. Please try again.';
      
      case 'E_USER_NOT_FOUND':
        return 'No account found with this email address.';
      
      case 'E_ACCOUNT_DISABLED':
        return 'This account has been disabled. Please contact support.';
      
      case 'E_TOO_MANY_REQUESTS':
        return 'Too many attempts. Please try again later.';
      
      case 'E_EMAIL_ALREADY_EXISTS':
        return 'An account with this email already exists. Please sign in instead.';
      
      case 'E_INVALID_VERIFICATION_CODE':
        return 'Invalid or expired verification code. Please try again or request a new code.';
      
      case 'E_VALIDATION_FAILURE':
        // Handle validation errors with details
        if (errorResponse.details && Array.isArray(errorResponse.details)) {
          const messages = errorResponse.details.map(detail => detail.message);
          return messages.join(' ');
        }
        return errorResponse.message || 'Validation failed. Please check your input.';
      
      default:
        break;
    }
  }
  
  // Handle error messages directly (backend sends message field with error codes)
  if (errorResponse?.message) {
    const message = errorResponse.message;
    
    // Map specific backend error codes to user-friendly messages
    if (message === 'E_OTP_EXPIRED') {
      return 'Your verification code has expired. Please request a new code.';
    }
    if (message === 'E_INVALID_OTP') {
      return 'Invalid verification code. Please check and try again.';
    }
    if (message === 'E_EMAIL_ALREADY_VERIFIED') {
      return 'Your email is already verified. Please sign in.';
    }
    
    // Return the message if it looks user-friendly
    if (!message.startsWith('E_') && message.length < 200) {
      return message;
    }
  }
  
  // Handle validation errors with details array
  if (errorResponse?.details && Array.isArray(errorResponse.details)) {
    const errorMessages = errorResponse.details
      .map(detail => detail.message)
      .filter(msg => msg) // Remove empty messages
      .join(' ');
    
    if (errorMessages) {
      return errorMessages;
    }
  }
  
  // Return error field if available
  if (errorResponse?.error) {
    return errorResponse.error;
  }
  
  // Fallback
  return 'An error occurred. Please try again.';
};

/**
 * Log error for debugging while showing user-friendly message
 * @param {string} context - Context where error occurred (e.g., 'Login', 'Registration')
 * @param {Error} error - The error object
 * @param {string} userMessage - The user-friendly message being shown
 */
export const logAuthError = (context, error, userMessage) => {
  console.error(`[${context}] Error:`, {
    userMessage,
    errorCode: error?.code,
    errorMessage: error?.message,
    fullError: error,
    timestamp: new Date().toISOString()
  });
};
